import { useMemo } from "react";
import { AttributeError, validateAttributeName } from "../../../../utils/validateAttributeName";
import { ImportAttribute } from "../../Import/types";

import { LayerAttribute } from "../components/Layer/LayerAttributeItem";
import { TableAttribute } from "../components/Table/TableAttributeItem";

type AttributesErrors = Map<TableAttribute["id"], AttributeError>;
type PickedTableAttr = Pick<TableAttribute, "id" | "name">;
type PickedLayerAttr = Pick<LayerAttribute, "attributeName">;
type PickedImportAttr = Pick<ImportAttribute, "disabled">;
type PartialAttribute = Partial<PickedTableAttr & PickedLayerAttr & PickedImportAttr>;

// eslint-disable-next-line max-len
export const useAttributesErrors = (
  attributes: PartialAttribute[],
  sysAttributes?: PartialAttribute[],
): AttributesErrors => {
  return useMemo<AttributesErrors>(() => {
    if (!attributes || !attributes.length) return new Map();

    const sysAttributesNamesSet = new Set<string>();

    if (sysAttributes?.length > 0) {
      sysAttributes.forEach(({ attributeName }) => sysAttributesNamesSet.add(attributeName));
    }

    const { errors } = attributes.reduce(
      (acc, attribute) => {
        if (attribute.id === void 0 || attribute.disabled) return acc;

        let errorType: Nullable<AttributeError> = null;

        const attributeName: string = attribute.attributeName || attribute.name;

        errorType = validateAttributeName(attributeName, acc.attributeNamesSet);

        if (errorType) {
          acc.errors.set(attribute.id, errorType);
        } else {
          acc.attributeNamesSet.add(attributeName);
        }

        return acc;
      },
      {
        attributeNamesSet: sysAttributesNamesSet,
        errors: new Map<TableAttribute["id"], AttributeError>(),
      },
    );

    return errors;
  }, [attributes, sysAttributes]);
};
